import { makeAutoObservable } from 'mobx';

import { defaultItemsPerPage, itemsPerPageCookieName, tabIds } from 'constants/ui';
import { OptionBase, SelectField } from 'store/fileds/SelectField';
import { cookie } from 'utils/storage';
import { followedLoadsItemsPerPageStorage } from 'utils/storage/Storage';

import type { RootStore } from '../RootStore';
import { TFollowedLoadsTabIds, TTabIds, followedLoadsTabIds } from './UIStore';

type TTabsWithPagination = TFollowedLoadsTabIds | Exclude<TTabIds, 'favorites'>;

type TPages = {
  [key in TTabsWithPagination]: number;
};

export const tabIdsWithPagination = {
  onlyWithOffers: 'onlyWithOffers',
  onlyWithFavorites: 'onlyWithFavorites',
  onlyWithAuctions: 'onlyWithAuctions',
  ...tabIds,
} as const;

const defaultCurrentPage = 1;
const pageKeepingFirstItem = (page: number, prevPerPage: number, nextPerPage: number) =>
  Math.ceil(((page - 1) * prevPerPage + 1) / nextPerPage);
const defaultPages = Object.keys(tabIdsWithPagination).reduce((acc, key) => {
  acc[key as TTabsWithPagination] = defaultCurrentPage;
  return acc;
}, {} as TPages);

class Pagination {
  root: RootStore;
  pages: TPages;
  loadsPerPage;
  followedLoadsPerPage;

  constructor(root: RootStore) {
    this.root = root;

    this.pages = defaultPages;
    this.loadsPerPage = new SelectField(this.root.options.getItemsPerPageParams);
    this.followedLoadsPerPage = {
      onlyWithFavorites: new SelectField(this.root.options.getItemsPerPageParams),
      onlyWithOffers: new SelectField(this.root.options.getItemsPerPageParams),
      onlyWithAuctions: new SelectField(this.root.options.getItemsPerPageParams),
    };

    makeAutoObservable(this);
  }

  get activeTab() {
    if (this.root.ui.activeTab === 'favorites') {
      return this.root.ui.followedLoadsTab.data;
    }

    return this.root.ui.activeTab;
  }

  get activeFollowedLoadsPerPage() {
    return this.followedLoadsPerPage[this.root.ui.followedLoadsTab.data];
  }

  get totalSearchPages() {
    if (!this.root.app.searchResult) return 0;

    const { rootCount, hiddenItems, totalItems } = this.root.app.searchResult;
    const totalRows = rootCount ?? totalItems - hiddenItems;

    return Math.ceil((totalRows - this.root.app.hiddenLoadsIncrement) / this.loadsPerPage.data);
  }

  get isLastSearchPage() {
    return this.pages.search === this.totalSearchPages;
  }

  get totalFavoritesPages() {
    return Math.ceil(this.root.followedLoads.favorites.count / this.followedLoadsPerPage.onlyWithFavorites.data);
  }

  get totalLoadsWithOffersPages() {
    return Math.ceil(this.root.followedLoads.loadsWithOffers.count / this.followedLoadsPerPage.onlyWithOffers.data);
  }

  get totalLoadsWithAuctionsPages() {
    return Math.ceil(this.root.followedLoads.loadsWithAuctions.count / this.followedLoadsPerPage.onlyWithAuctions.data);
  }

  get totalPages() {
    switch (this.activeTab) {
      case followedLoadsTabIds.onlyWithFavorites:
        return this.totalFavoritesPages;
      case followedLoadsTabIds.onlyWithOffers:
        return this.totalLoadsWithOffersPages;
      case followedLoadsTabIds.onlyWithAuctions:
        return this.totalLoadsWithAuctionsPages;
      default:
        return this.totalSearchPages;
    }
  }

  get currentPage() {
    return this.pages[this.activeTab];
  }

  setCurrentPage = (page: number) => {
    this.pages[this.activeTab] = page;
  };

  setLoadsPerPage = (option: OptionBase<number>) => {
    this.setCurrentPage(defaultCurrentPage);
    this.loadsPerPage.setOption(option);
    cookie.set({ name: itemsPerPageCookieName, value: String(option.value), days: 365 });
  };

  setFollowedLoadsPerPage = (option: OptionBase<number>) => {
    const tab = this.root.ui.followedLoadsTab.data;
    const perPage = this.followedLoadsPerPage[tab];

    this.pages[tab] = pageKeepingFirstItem(this.pages[tab], perPage.data, option.value);
    perPage.setOption(option);
    followedLoadsItemsPerPageStorage.set({ ...followedLoadsItemsPerPageStorage.get(), [tab]: option.value });
  };

  init = () => {
    let itemsPerPage: string | void;

    if (!this.root.profile.data?.isUser || !this.root.profile.data?.isPaidUser) {
      itemsPerPage = defaultItemsPerPage;
      cookie.set({ name: itemsPerPageCookieName, value: itemsPerPage, days: 365 });
    } else {
      itemsPerPage = cookie.get(itemsPerPageCookieName);
    }

    if (itemsPerPage) {
      this.loadsPerPage.setOptionByValue(Number(itemsPerPage));
    }

    const followedLoadsItemsPerPage = followedLoadsItemsPerPageStorage.get();

    Object.values(followedLoadsTabIds).forEach(tab => {
      const itemsPerPage = followedLoadsItemsPerPage?.[tab];

      if (itemsPerPage) {
        this.followedLoadsPerPage[tab].setOptionByValue(itemsPerPage);
      }
    });
  };
}

export { Pagination };
